import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock } from "lucide-react";

interface JourneyComingSoonStepProps {
  userId: string;
  /** "declaration" or "affiliation" */
  slot?: string;
  message?: string;
}

/**
 * Additive placeholder step. Renders a quiet coming soon card.
 * The affiliation slot also reads the member's affiliation status so the
 * card can show the live state once the instruments ship.
 */
export default function JourneyComingSoonStep({ userId, slot, message }: JourneyComingSoonStepProps) {
  const [affiliationStatus, setAffiliationStatus] = useState<string | null>(null);

  useEffect(() => {
    if (slot !== "affiliation" || !userId) return;
    const load = async () => {
      const { data } = await (supabase as any)
        .from("member_records")
        .select("affiliation_status")
        .eq("user_id", userId)
        .maybeSingle();
      setAffiliationStatus(data?.affiliation_status ?? null);
    };
    void load();
  }, [slot, userId]);

  const statusLabel =
    affiliationStatus === "active"
      ? "Your affiliation is active."
      : affiliationStatus === "lapsed"
        ? "Your affiliation is due for renewal."
        : null;

  return (
    <Card className="border-dashed">
      <CardContent className="pt-6 space-y-3">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span className="text-sm">{message || "This step is being prepared for you."}</span>
        </div>
        {statusLabel && <Badge variant="secondary">{statusLabel}</Badge>}
      </CardContent>
    </Card>
  );
}
